import SwitchConnectivityState, {
  SwitchStackData,
  Switch,
  FlatSwitchData,
  Port,
  VlanLegendData,
} from '../interfaces/SwitchConnectivityState';
import InsightsAction from 'redux/Action';
import flattenSwitchData from './flattenSwitchData';
import formatDefaultSwitchData from './formatDefaultSwitchData';
import SwitchResponseData from '../interfaces/response/SwitchResponseData';
import PortHistoryResponseData from '../interfaces/response/PortHistoryResponseData';
import formatPortHistoryData from './formatPortHistoryData';

import {
  PortHistoryPort,
  PortHistorySwitch,
  PortHistorySwitchStack,
} from '../interfaces/PortHistoryState';

export const initialState: SwitchConnectivityState = {
  switchStacks: [],
  flattenedSwitchData: [],
  filteredFlattenedSwitchData: [],
  legendData: [],
  selectedVLANs: [],
  hasPreassignedColor: false,
  switchStackNames: [],
  isSwitchDataReady: false,
  portHistoryState: {switchStacks: {}},
  isPortHistoryDataReady: false,
  searchText: '',
  searchOptions: {
    includeCurrentData: true,
    includeHistoricalData: false,
  },
};

export const reducer = (
  state: SwitchConnectivityState,
  action: InsightsAction
): SwitchConnectivityState => {
  switch (action.type) {
    case 'LEGEND_ITEM_SELECTION_CHANGED': {
      const {switchStackName} = action.payload;
      const {selectedVLANs, legendData} = handleLegendItemSelectionChanged(state, action);
      const {switchStacks, filteredFlattenedSwitchData} = filterPortsFromSearchAndLegendChange(
        {...state, selectedVLANs, legendData},
        switchStackName
      );
      return {
        ...state,
        selectedVLANs,
        legendData,
        switchStacks,
        filteredFlattenedSwitchData,
      };
    }
    case 'LEGEND_ITEM_CLEAR': {
      return handleClearSelectedVLAN(state, action.payload);
    }
    case 'SEARCH_TEXT_CHANGED': {
      const {searchText, searchOptions, switchStackName} = action.payload;
      const filteredSearchText = searchText.replace(/[.|:]/g, '').toLowerCase();
      const {switchStacks, filteredFlattenedSwitchData} = filterPortsFromSearchAndLegendChange(
        {...state, searchText: filteredSearchText, searchOptions},
        switchStackName
      );
      return {
        ...state,
        switchStacks,
        filteredFlattenedSwitchData,
        searchText: filteredSearchText,
        searchOptions,
      };
    }
    case 'UPDATE_SWITCH_DATA': {
      return updateSwitchDataFromAPI(state, action.payload.data, action.payload.deviceNames);
    }
    case 'UPDATE_PORT_HISTORY_DATA': {
      return updatePortHistoryDataFromAPI(state, action.payload);
    }
    case 'RESET_SWITCH_CONNECTIVITY': {
      return {
        ...state,
        isSwitchDataReady: false,
      };
    }
    default: {
      return state;
    }
  }
};

//#region LEGEND_ITEM_SELECTION_CHANGED
function handleLegendItemSelectionChanged(
  state: SwitchConnectivityState,
  action: any
): SwitchConnectivityState {
  const switchStackName = action.payload.switchStackName;
  const vlanNumber = action.payload.vlanNumber;
  const isSelected = action.payload.isSelected;

  const selectedVLANs = updateSelectedVLANs(state, vlanNumber, isSelected, switchStackName);
  const legendData = updateLegendSelection(state, vlanNumber, isSelected, switchStackName);

  return {
    ...state,
    selectedVLANs,
    legendData,
  };
}

function handleClearSelectedVLAN(
  state: SwitchConnectivityState,
  switchStackName: string
): SwitchConnectivityState {
  const selectedVLANs: {vlanNumber: string; switchStackName: string}[] = [];
  const legendData = resetVLANLegend(state);

  const switchStacks: SwitchStackData[] = state.switchStacks.map(switchStack => {
    if (switchStack.switchStackName !== switchStackName) {
      return switchStack;
    }
    const memberSwitches: Switch[] = switchStack.memberSwitches.map(switchItem => {
      const mainPorts: Port[] = switchItem.mainPorts.map(switchPort => {
        return {...switchPort, isSelected: true};
      });
      return {
        ...switchItem,
        mainPorts,
      };
    });
    return {
      ...switchStack,
      memberSwitches,
    };
  });

  const filteredFlattenedSwitchData: FlatSwitchData[] = updateFilteredFlattenedSwitchData(
    state,
    selectedVLANs
  );

  return {
    ...state,
    selectedVLANs,
    legendData,
    switchStacks,
    filteredFlattenedSwitchData,
    searchText: '',
  };
}

function updateSelectedVLANs(
  state: SwitchConnectivityState,
  vlanNumber: string,
  isSelected: boolean,
  switchStackName: string
): {vlanNumber: string; switchStackName: string}[] {
  if (isSelected) {
    return [...state.selectedVLANs, {vlanNumber, switchStackName}];
  } else {
    return state.selectedVLANs.filter(vlan => vlan.vlanNumber !== vlanNumber);
  }
}

function updateLegendSelection(
  state: SwitchConnectivityState,
  vlanNumber: string,
  isSelected: boolean,
  switchStackName: string
): VlanLegendData[] {
  return state.legendData.map(legendItem => {
    if (legendItem.switchDeviceName !== switchStackName) {
      return legendItem;
    }

    const selectedVLAN = vlanNumber;
    const updatedIsSelected =
      legendItem.vlanNumber === selectedVLAN ? isSelected : legendItem.isSelected;

    return {...legendItem, isSelected: updatedIsSelected};
  });
}

function updateFilteredFlattenedSwitchData(
  state: SwitchConnectivityState,
  selectedVLANs: {vlanNumber: string; switchStackName: string}[]
): FlatSwitchData[] {
  if (selectedVLANs.length === 0) {
    return state.flattenedSwitchData;
  } else {
    const selectedVLANsWithoutConnectedPort = selectedVLANs.filter(
      vlan => vlan.vlanNumber !== 'Plug Icon'
    );

    const vlanNumbers = selectedVLANsWithoutConnectedPort.map(vlan => vlan.vlanNumber.toString());

    return state.flattenedSwitchData.filter(switchPort => {
      return vlanNumbers.includes(switchPort.vlanNumber.toString());
    });
  }
}
//#endregion LEGEND_ITEM_SELECTION_CHANGED

//#region RESET_VLAN_LEGEND
function resetVLANLegend(state: SwitchConnectivityState) {
  return state.legendData.map(legendItem => {
    return {...legendItem, isSelected: false};
  });
}
//#endregion RESET_VLAN_LEGEND

//#region UPDATE_SWITCH_DATA
function updateSwitchDataFromAPI<SwitchConnectivityState>(
  state: SwitchConnectivityState,
  apiData: SwitchResponseData,
  deviceNames: string[]
) {
  let filteredSwitchStacks = apiData.switchStacks;
  if (deviceNames.length > 0) {
    filteredSwitchStacks = apiData.switchStacks.filter(switchStack =>
      deviceNames.includes(switchStack.switchStackName)
    );
  }

  let uniqueSwitchNames = new Set<string>();
  let uniqueVlanNumbers = new Set<string>();

  filteredSwitchStacks.forEach(switchStack => {
    uniqueSwitchNames.add(switchStack.switchStackName);
    switchStack.memberSwitches.forEach(switchItem => {
      switchItem.mainPorts.forEach(port => {
        uniqueVlanNumbers.add(port.vlanNumber);
        if (port.vlanNumber === 'Trunk' || port.vlanNumber === 'Trunk Port') {
          uniqueVlanNumbers.add('Trunk Port');
        }
      });
    });
  });

  const filteredVlanLegendItems = apiData.vlanLegendItems.filter(legendItem =>
    uniqueVlanNumbers.has(legendItem.vlanNumber)
  );
  const collator = new Intl.Collator('en', {numeric: true, sensitivity: 'base'});
  const sortedVlanLegendItems = filteredVlanLegendItems.sort((a, b) =>
    collator.compare(a.vlanNumber, b.vlanNumber)
  );

  const connectedVlanLegendItems = Array.from(uniqueSwitchNames).map(switchStackName => {
    return {
      switchDeviceName: switchStackName,
      vlanNumber: 'Plug Icon',
      color: '#000000',
      shortDescription: 'Device connected to port',
      isSelected: false,
    };
  });

  const filteredApiData = {
    ...apiData,
    vlanLegendItems: [...sortedVlanLegendItems, ...connectedVlanLegendItems],
    switchStacks: filteredSwitchStacks,
  };

  const {switchStacks, legendData, switchStackNames} = formatDefaultSwitchData(filteredApiData);

  const flattenedSwitchData = flattenSwitchData(filteredApiData);

  const filteredFlattenedSwitchData = [...flattenedSwitchData];
  return {
    ...state,
    flattenedSwitchData,
    filteredFlattenedSwitchData,
    legendData,
    switchStacks,
    switchStackNames,
    isSwitchDataReady: true,
    selectedVlans: [],
  };
}
//#endregion UPDATE_SWITCH_DATA

//#region FILTER_PORTS_FROM_SEARCH_AND_LEGEND_CHANGE
function filterPortsFromSearchAndLegendChange(
  state: SwitchConnectivityState,
  switchStackName: string
) {
  const {searchText, searchOptions, selectedVLANs} = state;

  // Filtered Main Ports by VLANs
  const switchStacksFilteredByVLANs = state.switchStacks.map(switchStack => {
    if (switchStack.switchStackName !== switchStackName) {
      return switchStack;
    }

    const memberSwitches = switchStack.memberSwitches.map(switchItem => {
      const mainPorts = switchItem.mainPorts.map(switchPort => {
        const isSelected =
          selectedVLANs.length === 0 ||
          selectedVLANs.some(vlan => vlan.vlanNumber === switchPort.vlanNumber) ||
          (selectedVLANs.some(vlan => vlan.vlanNumber === 'VLAN') &&
            switchPort.vlanNumber === null) ||
          (selectedVLANs.some(vlan => vlan.vlanNumber === 'Plug Icon') &&
            switchPort.portStatus === 'connected');

        return {...switchPort, isSelected};
      });

      return {
        ...switchItem,
        mainPorts,
      };
    });

    return {
      ...switchStack,
      memberSwitches,
    };
  });

  const switchStacksFilteredBySearch: SwitchStackData[] = switchStacksFilteredByVLANs.map(
    switchStack => {
      if (switchStack.switchStackName !== switchStackName) {
        return switchStack;
      }

      const portHistorySwitchStack: PortHistorySwitchStack =
        state.portHistoryState.switchStacks[switchStack.switchStackName];

      const memberSwitches: Switch[] = switchStack.memberSwitches.map(switchItem => {
        const portHistorySwitch: PortHistorySwitch =
          portHistorySwitchStack?.switches[switchItem.switchNumber];

        const mainPorts: Port[] = switchItem.mainPorts.map(switchPort => {
          if (
            searchOptions.includeCurrentData &&
            switchPort.searchData.includes(searchText) &&
            switchPort.isSelected
          ) {
            return {...switchPort, isSelected: true};
          }

          const portHistoryPort: PortHistoryPort = portHistorySwitch?.ports[switchPort.portNumber];
          if (
            searchOptions.includeHistoricalData &&
            portHistoryPort?.snapshots?.length > 0 &&
            portHistoryPort.snapshots.filter(s => s.searchData.includes(searchText)).length > 0
          ) {
            return {...switchPort, isSelected: true};
          }

          return {...switchPort, isSelected: false};
        });
        return {
          ...switchItem,
          mainPorts,
        };
      });
      return {
        ...switchStack,
        memberSwitches,
      };
    }
  );

  // Filtered Flattened Switch Data by VLANs
  function filterFlattenedSwitchDataByVLANs(flattenedSwitchData: FlatSwitchData[]) {
    if (selectedVLANs.length === 0) {
      return flattenedSwitchData;
    } else {
      const selectedVLANsWithoutConnectedPort = selectedVLANs.filter(
        vlan => vlan.vlanNumber !== 'Plug Icon'
      );

      const vlanNumbers = selectedVLANsWithoutConnectedPort.map(vlan => vlan.vlanNumber.toString());

      return state.flattenedSwitchData.filter(switchPort => {
        return vlanNumbers.includes(switchPort.vlanNumber.toString());
      });
    }
  }

  const filteredFlattenedSwitchByVLANs: FlatSwitchData[] = filterFlattenedSwitchDataByVLANs(
    state.flattenedSwitchData
  );

  const filteredFlattenedSwitchBySearch = filteredFlattenedSwitchByVLANs.filter(switchPort => {
    if (switchPort.switchStackName !== switchStackName) return true;

    if (searchOptions.includeCurrentData && switchPort.searchData.includes(searchText)) return true;

    if (searchOptions.includeHistoricalData) {
      const portHistory: PortHistoryPort =
        state.portHistoryState?.switchStacks[switchPort.switchStackName]?.switches[
          switchPort.switchNumber
        ]?.ports[switchPort.portNumber];

      if (!portHistory) return false;

      return portHistory.snapshots.filter(s => s.searchData.includes(searchText)).length > 0;
    }

    return false;
  });

  return {
    ...state,
    switchStacks: switchStacksFilteredBySearch,
    filteredFlattenedSwitchData: filteredFlattenedSwitchBySearch,
  };
}
//#endregion FILTER_PORTS_FROM_SEARCH_AND_LEGEND_CHANGE

//#region UPDATE_PORT_HISTORY_DATA
function updatePortHistoryDataFromAPI<SwitchConnectivityState>(
  state: SwitchConnectivityState,
  apiData: PortHistoryResponseData[]
) {
  const formattedPortHistoryData = formatPortHistoryData(apiData);
  return {
    ...state,
    portHistoryState: formattedPortHistoryData,
    isPortHistoryDataReady: true,
  };
}

//#endregion
